/**
 * The "May I?" banner. A card has been thrown and everyone gets the time to ask the host
 * chose (1 to 15 seconds). The countdown is a shrinking ring plus a numeral, never text alone. §8.2.
 */

import { useEffect, useState } from 'react';
import type { Card } from '@garbage/shared';
import { CardFace } from './Card.js';
import type { PackId } from '../packs.js';
import { useT } from '../i18n.js';

export function claimSecondsLeft(closesAt: number | null, now: number): number {
  if (closesAt === null) return 0;
  return Math.max(0, Math.ceil((closesAt - now) / 1000));
}

export function claimFraction(closesAt: number | null, now: number, durationMs: number): number {
  if (closesAt === null || durationMs <= 0) return 0;
  return Math.max(0, Math.min(1, (closesAt - now) / durationMs));
}

/** Someone who has asked for the card, in the order the tie-break would give it out. */
export type Asker = { id: string; name: string; you: boolean };

export function ClaimBanner({
  card, pack, durationMs, closesAt, youMayClaim, costsPenalty, alreadyClaimed, askers, thrownBy, uncovered = false,
  thrownByYou = false, onClaim,
}: {
  card: Card;
  pack: PackId;
  durationMs: number;
  closesAt: number | null;
  youMayClaim: boolean;
  /** True when winning costs a penalty card — false for the player whose turn is next. */
  costsPenalty: boolean;
  alreadyClaimed: boolean;
  /** Everyone asking, first in line first — so you can see who wants what. */
  askers: Asker[];
  /** Name of whoever threw it; null for the card turned up at the deal, or one uncovered. */
  thrownBy: string | null;
  /** Uncovered when the card above it was taken — open to everybody. §3.5. */
  uncovered?: boolean;
  /** You threw it yourself, which reads differently from somebody else's name. */
  thrownByYou?: boolean;
  onClaim: () => void;
}) {
  const t = useT();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    // 10fps is plenty for a ring and a numeral, and is kind to a cheap phone.
    const handle = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(handle);
  }, []);

  const seconds = claimSecondsLeft(closesAt, now);
  const fraction = claimFraction(closesAt, now, durationMs);

  return (
    /*
     * In the layout, never over it. This used to be pinned to the bottom of the
     * screen, which covered the player's own hand — at exactly the moment they need
     * to look at it to decide whether they want the card.
     */
    <section
      data-testid="claim-banner"
      aria-live="polite"
      className="mx-3 mb-1 rounded-2xl border border-sun/40 bg-sun/10 px-3 py-2"
    >
      <div className="mx-auto flex max-w-xl items-center gap-3">
        <Ring fraction={fraction} seconds={seconds} />
        <CardFace card={card} size="medium" pack={pack} />

        <div className="flex-1">
          <p className="text-sm font-bold text-sun">
            {uncovered
              ? t('claim.nextCardDown')
              : thrownByYou ? t('claim.youThrewIt')
                : thrownBy ? t('claim.theyThrewIt', { name: thrownBy }) : t('claim.thrownAway')}
          </p>
          {askers.length > 0 ? (
            <ul className="flex flex-wrap gap-1 pt-0.5" data-testid="claim-askers">
              {askers.map((asker, i) => (
                <li
                  key={asker.id}
                  data-testid={`asker-${asker.id}`}
                  className={[
                    'rounded-full px-2 py-0.5 text-[11px] font-bold',
                    i === 0 ? 'bg-violet-300 text-ink' : 'bg-violet-400/25 text-violet-100',
                  ].join(' ')}
                >
                  🙋 {asker.you ? t('common.you') : asker.name}
                  {i === 0 && askers.length > 1 && <span className="font-normal">{t('claim.firstInLine')}</span>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-bone/70">{t('claim.nobodyAsked')}</p>
          )}
        </div>

        {youMayClaim && !alreadyClaimed && (
          <button
            type="button"
            data-testid="claim-button"
            onClick={onClaim}
            className="rounded-2xl bg-sun px-4 py-3 text-left font-black text-ink shadow-lg active:scale-95"
          >
            <span className="block text-lg leading-none">{t('claim.mayI')}</span>
            {/* Say what it costs before they tap, not after. §8.2 */}
            <span data-testid="claim-cost" className="block text-[10px] font-bold leading-tight opacity-70">
              {costsPenalty ? t('claim.costsCard') : t('claim.free')}
            </span>
          </button>
        )}
        {alreadyClaimed && (
          <span data-testid="claim-pending" className="rounded-2xl bg-white/15 px-4 py-3 text-sm font-bold">
            {t('claim.asked')}
          </span>
        )}
        {!youMayClaim && !alreadyClaimed && (
          <span data-testid="claim-reason" className="max-w-24 text-right text-xs text-bone/60">
            {/* Nobody is barred from a card uncovered further down, so it can only be the limit. */}
            {uncovered ? t('claim.noAsksLeft') : t('claim.youThrewIt')}
          </span>
        )}
      </div>
    </section>
  );
}

function Ring({ fraction, seconds }: { fraction: number; seconds: number }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;

  return (
    <div className="relative h-12 w-12 shrink-0" data-testid="claim-ring" data-seconds={seconds}>
      <svg viewBox="0 0 48 48" className="h-12 w-12 -rotate-90">
        <circle cx="24" cy="24" r={radius} fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="4" />
        <circle
          cx="24" cy="24" r={radius} fill="none" stroke="var(--color-sun)" strokeWidth="4"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-black">
        {seconds}
      </span>
    </div>
  );
}
